
import { Component } from '@angular/core';
import { UserLoginStatsService, LoginStatsResponse, LoginLog } from '@core/services/user-login-stats.service';
import { CommonModule, NgClass, NgIf, NgFor, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-home-backoffice',
  templateUrl: './home-backoffice.component.html',
  styleUrls: ['./home-backoffice.component.css'],
  standalone: true,
  imports: [CommonModule, NgClass, NgIf, NgFor, DatePipe, FormsModule]
})

export class HomeBackofficeComponent {
  loginStats: LoginStatsResponse | null = null;
  loginLogs: LoginLog[] = [];
  allLoginLogs: LoginLog[] = []; // Todos los logs sin filtrar por fecha
  filteredLoginLogs: LoginLog[] = []; // Logs filtrados por fecha y usuario
  loginLogsPage = 1;
  loginLogsLastPage = 1;
  logsPerPage = 50;
  isAllowed = false;
  isLoading = true;  // Spinner de carga principal
  selectedUser: {id: number, name: string, email: string} | null = null;
  uniqueUsers: {id: number, name: string, email: string}[] = [];
  allUsersLoaded = false;
  isUsersLoading: boolean = false;
  
  // Filtro de días
  selectedDays: number = 30;
  dayOptions = [
    { value: 7, label: '7 días' },
    { value: 15, label: '15 días' }, 
    { value: 30, label: '30 días' }
  ];

  // Validación de filtro de fechas
  isFilteringByDates(): boolean {
    return this.selectedDays > 0 && this.allLoginLogs.length > this.filteredLoginLogs.length;
  }

  constructor(
    private userLoginStatsService: UserLoginStatsService
  ) {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.roles && Array.isArray(user.roles)) {
      this.isAllowed = user.roles.some((role: any) => role.id === 1 || role.id === 2 || role.name === 'admin' || role.name === 'superusuario');
    }
    if (this.isAllowed) {
      this.loadStats();
      this.loadLogs();
      this.loadAllUsers(); // Cargar todos los usuarios disponibles
    }
  }

  loadAllUsers() {
    // Cargar usuarios sin filtro de días para tener la lista completa
    this.isUsersLoading = true;
    this.userLoginStatsService.getLoginLogs(1000, 1, 'success', 'email', undefined, undefined).subscribe(res => {
      const usersMap: {[id: number]: {id: number, name: string, email: string}} = {};
      res.data.forEach(log => {
        if (log.user && log.user.id) {
          usersMap[log.user.id] = log.user;
        }
      });
      this.uniqueUsers = Object.values(usersMap);
      this.isUsersLoading = false;
    }, error => {
      console.error('Error al cargar usuarios:', error);
      this.isUsersLoading = false;
    });
  }

  loadStats() {
    this.userLoginStatsService.getLoginStats(this.selectedDays).subscribe(stats => {
      this.loginStats = stats;
      this.checkLoadingComplete();
    }, error => {
      console.error('loginStats error', error);
      this.checkLoadingComplete();
    });
  }

  loadLogs() {
    // Cargar todos los logs sin filtro de fecha (máximo período para tener todos los datos)
    this.userLoginStatsService.getLoginLogs(1000, 1, 'success', 'email', undefined, undefined).subscribe(res => {
      this.allLoginLogs = res.data;
      this.applyFilters();
      this.checkLoadingComplete();
    }, error => {
      console.error('loginLogs error', error);
      this.checkLoadingComplete();
    });
  }

  applyFilters() {
    let filtered = [...this.allLoginLogs];

    // Filtrar por fecha (últimos X días)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.selectedDays);

    filtered = filtered.filter(log => {
      const logDate = new Date(log.logged_in_at);
      return logDate >= cutoffDate;
    });

    // Filtrar por usuario si está seleccionado
    if (this.selectedUser) {
      filtered = filtered.filter(log => 
        log.user && 
        log.user.id === this.selectedUser!.id
      );
    }

    // Ordenar por fecha más reciente primero
    filtered.sort((a, b) => new Date(b.logged_in_at).getTime() - new Date(a.logged_in_at).getTime());

    this.filteredLoginLogs = filtered;
    this.updatePagination();
  }

  updatePagination() {
    // Calcular paginación para los logs filtrados
    const totalItems = this.filteredLoginLogs.length;
    this.loginLogsLastPage = Math.ceil(totalItems / this.logsPerPage);
    
    // Asegurar que la página actual no exceda el máximo
    if (this.loginLogsPage > this.loginLogsLastPage) {
      this.loginLogsPage = 1;
    }

    // Obtener los logs para la página actual
    const startIndex = (this.loginLogsPage - 1) * this.logsPerPage;
    const endIndex = startIndex + this.logsPerPage;
    this.loginLogs = this.filteredLoginLogs.slice(startIndex, endIndex);
  }
  onUserChange() {
    // Si el usuario no selecciona nada o selecciona 'Todos', forzar null
    if (!this.selectedUser || (typeof this.selectedUser === 'string' && this.selectedUser === 'null')) {
      this.selectedUser = null;
    }
    // Reset paginación cuando cambie el filtro de usuario
    this.loginLogsPage = 1;
    this.applyFilters();
  }

  onDaysChange() {
    // Reset paginación y recargar datos cuando cambie el filtro de días
    this.loginLogsPage = 1;
    this.loadStats();
    this.applyFilters();
  }

  onLoginLogsPageChange(page: number) {
    this.loginLogsPage = page;
    this.updatePagination();
  }

  private checkLoadingComplete() {
    // El loading se completa cuando tenemos estadísticas y logs iniciales
    if (this.loginStats !== null && this.loginLogs !== undefined) {
      this.isLoading = false;
    }
  }
}